import {
    getOrganizations,
    organizationSelector,
    ORGANIZATIONS_QUERY_KEY,
    ORGANIZATIONS_QUERY_CONFIG
} from '@hotjar/organization';
import { queryClient } from '@hotjar/webapp/utils/react-query';

// eslint-disable-next-line no-redeclare
import _ from 'lodash';

/**
 * Organization service which includes actions related to an Organization.
 */
angular.module('hjApp').service('Organization', [
    '$rootScope',
    '$q',
    'Feature',
    'Loader',
    'ModalPrompt',
    'Notify',
    'Track',
    'Restangular',
    'restDefaultHandlers',
    'UserData',
    function ($rootScope, $q, Feature, Loader, ModalPrompt, Notify, Track, Restangular, restDefaultHandlers, UserData) {
        /********************
         * PUBLIC ACCESS FUNCTIONS
         ********************/
        /**
         * Get the users for an Organization.
         *
         * @param organization Organization to get users for. Defaults to the current site's organization.
         */
        this.users = function users(organization) {
            const organizationId = organization ? organization.id : UserData.currentSite.organization.id;
            const userId = UserData.get('userId');

            return queryClient
                .fetchQuery([ORGANIZATIONS_QUERY_KEY, organizationId, userId], () => getOrganizations(userId), {
                    ...ORGANIZATIONS_QUERY_CONFIG
                })
                .then(function (organizations) {
                    const organization = organizationSelector(organizationId)(organizations);

                    return organization?.users;
                })
                .catch(function () {
                    return [];
                });
        };

        /**
         * Create a new organization – used primarily through the site-create controller.
         *
         * ToDo: Improve this service to also cater for creating organizations in Billing V1: same API - requires plan).
         *
         * @param account Account to add new organization to.
         * @param name Name to use for new Organization.
         * @param posCallback function to run on success.
         */
        this.create = function create(account, name, posCallback) {
            // Do not proceed if we don't have all the details.
            if (_.isUndefined(account) || _.isUndefined(name)) {
                throw new Error('Organization.create(): You must specify (pass) an account and an organization name.');
            }

            // The plan is always set to Basic – handled through the API.
            var rOrganizations = Restangular.one('accounts', account.id).all('organizations');
            return rOrganizations
                .customPOST({
                    action: 'add_organization',
                    name: name
                })
                .then(
                    function (res) {
                        if (res.success) {
                            UserData.set('organizations', res.organizations);
                            Track.add('Organization');

                            if (_.isFunction(posCallback)) {
                                posCallback(res);
                            }

                            return $q.resolve();
                        } else {
                            Notify.error('Failed to create site.');
                            return $q.reject();
                        }
                    },
                    function () {
                        Notify.error('Failed to create site.');
                        return $q.reject();
                    }
                );
        };

        /**
         * Delete an organization on either Billing V1 and V2.
         *
         * NOTE: For billing v2, an Organization can only be deleted if all sites within it are on the Basic plan.
         *
         * ToDo: Billing v2: Handle deletions on paid plans.
         *
         * @param organization The organization to delete
         * @param posCallback Callback function to call on success.
         * @param negCallback Callback function to call on failure.
         */
        this.delete = function (organization, posCallback, negCallback) {
            var hasPaidPlans = _.some(organization.sites, function (site) {
                return site.plan !== 'basic';
            });

            if (hasPaidPlans) {
                Notify.error(
                    'To delete this Organization, please downgrade all of its sites to Basic from the ' +
                        'Plans & Billing page first.'
                );
                return;
            }

            ModalPrompt.show(
                'Are you REALLY sure?',
                'Deleting the organization "' +
                    organization.name +
                    '" will also delete all sites in that organization together with all their data.',
                'Yes, delete',
                'Cancel',
                function () {
                    Loader.show('Deleting organization, please wait...');

                    organization.remove().then(
                        function (res) {
                            Loader.hide();

                            if (res.success) {
                                Notify.info('Organization "' + organization.name + '" deleted.');

                                UserData.refreshFromServer(function () {
                                    if (_.isFunction(posCallback)) {
                                        posCallback();
                                        $rootScope.$emit('organizationDeleted', organization);
                                    }
                                });
                            } else {
                                if (_.isFunction(negCallback)) {
                                    negCallback(res);
                                }
                            }
                        },
                        function (res) {
                            restDefaultHandlers.onError(res);
                        }
                    );
                }
            );
        };
    }
]);
